import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { ConfirmationModalComponent } from '../../shared/confirmation-modal/confirmation-modal.component';
import { extractData, extractMessage, extractErrorMessage, ApiResponse } from '../../../helpers/api-response.helper';

@Component({
  selector: 'app-view-product-details',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ConfirmationModalComponent],
  templateUrl: './view-product-details.component.html',
  styleUrls: ['./view-product-details.component.css']
})
export class ViewProductDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('suppliersTableContainer') suppliersTableContainer?: ElementRef<HTMLDivElement>;
  
  product: any = null;
  loading = false;
  errorMessage = '';
  activeTab: 'details' | 'requirements' | 'share' | 'quote' = 'details';
  
  // Share tab filters
  searchTerm = '';
  selectedSupplierStatus = '';
  selectedQuoteStatus = '';
  allFilteredSuppliers: any[] = []; // All filtered suppliers (client-side filtered)
  displayedSuppliers: any[] = []; // Suppliers currently displayed (paginated)
  
  // Pagination (Infinite Scroll) for Share tab
  currentPage = 1;
  itemsPerPage = 10; // Show 10 items per load
  totalPages = 1;
  isLoadingMore = false; // Track if loading more data
  hasMoreData = true; // Track if there's more data to load
  
  // Action menu state
  showActionMenu: { [key: string]: boolean } = {};
  
  // Confirmation modal state
  showUnshareModal = false;
  quoteToUnshare: any = null;
  supplierToUnshare: any = null;
  unshareModalMessage = '';
  
  // Image modal state
  showImageModal = false;
  modalImageUrl = '';
  modalImageName = '';
  
  // Quote tab state
  quotes: any[] = [];
  filteredQuotes: any[] = [];
  selectedQuotes: Set<string> = new Set();
  quoteSearchTerm = '';
  quoteStatusFilter = '';
  quotesLoading = false;
  
  // Quote comparison state
  showCompareTab = false;
  comparisonData: any = null;
  comparisonQuantities: any[] = [];
  comparisonSuppliers: any[] = [];
  
  private scrollHandler: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    public authService: AuthService
  ) {}

  ngOnInit() {
    const productId = this.route.snapshot.paramMap.get('id');
    if (productId) {
      this.loadProductDetails(productId);
    }
    
    // Check for tab query parameter (both snapshot and subscription for initial load and navigation)
    const tabParam = this.route.snapshot.queryParams['tab'];
    
    // If supplier tries to access 'share' or 'quote' tabs, redirect to 'details'
    if (this.authService.isSupplier() && (tabParam === 'share' || tabParam === 'quote')) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { tab: null },
        queryParamsHandling: 'merge'
      });
      this.activeTab = 'details';
    } else if (tabParam === 'share' && !this.authService.isSupplier()) {
      this.activeTab = 'share';
    }
    
    // Subscribe to query params changes
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'];
      
      // If supplier tries to access 'share' or 'quote' tabs, redirect to 'details'
      if (this.authService.isSupplier() && (tab === 'share' || tab === 'quote')) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { tab: null },
          queryParamsHandling: 'merge'
        });
        this.activeTab = 'details';
      } else if (tab === 'share' && !this.authService.isSupplier()) {
        this.activeTab = 'share';
        // Apply filters and setup scroll listener after a short delay to ensure DOM is ready
        setTimeout(() => {
          this.applyShareFilters();
          this.attachScrollListener();
        }, 100);
      }
    });
  }

  ngAfterViewInit() {
    // Attach scroll listener after view is initialized
    this.attachScrollListener();
    // Add keyboard event listener for closing modal with Escape key
    document.addEventListener('keydown', this.handleKeyDown);
  }

  ngOnDestroy() {
    // Remove scroll event listener when component is destroyed
    if (this.scrollHandler && this.suppliersTableContainer && this.suppliersTableContainer.nativeElement) {
      this.suppliersTableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
    }
    // Remove keyboard event listener
    document.removeEventListener('keydown', this.handleKeyDown);
    // Close action menus
    this.closeActionMenus();
    // Restore body scroll if modal was open
    if (this.showImageModal) {
      document.body.style.overflow = '';
    }
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.showImageModal) {
      this.closeImageModal();
    }
  }

  attachScrollListener() {
    if (this.suppliersTableContainer && this.suppliersTableContainer.nativeElement) {
      // Remove existing listener if any
      if (this.scrollHandler) {
        this.suppliersTableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      }
      
      // Add scroll event listener to table container with throttling
      this.scrollHandler = this.throttle(() => {
        this.onSuppliersScroll();
      }, 200); // Throttle to 200ms to prevent too many calls
      
      this.suppliersTableContainer.nativeElement.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  // Throttle function to limit scroll event calls
  private throttle(func: Function, limit: number) {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, ...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  onSuppliersScroll() {
    if (!this.suppliersTableContainer || !this.suppliersTableContainer.nativeElement) {
      return;
    }

    const container = this.suppliersTableContainer.nativeElement;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Load more when scrolled within 100px of bottom
    const threshold = 100;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    
    if (isNearBottom && this.hasMoreData && !this.isLoadingMore && this.activeTab === 'share') {
      this.loadMoreSuppliers();
    }
  }

  loadMoreSuppliers() {
    // Don't load if already loading or no more data
    if (this.isLoadingMore || !this.hasMoreData) {
      return;
    }

    // Check if there are more pages
    if (this.currentPage < this.totalPages) {
      this.isLoadingMore = true;
      const nextPage = this.currentPage + 1;
      const startIndex = (nextPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      
      // Get next batch of suppliers
      const nextBatch = this.allFilteredSuppliers.slice(startIndex, endIndex);
      this.displayedSuppliers = [...this.displayedSuppliers, ...nextBatch];
      
      this.currentPage = nextPage;
      this.hasMoreData = this.currentPage < this.totalPages;
      
      // Re-attach scroll listener after new data loads
      setTimeout(() => {
        this.attachScrollListener();
        this.isLoadingMore = false;
      }, 50);
    } else {
      this.hasMoreData = false;
      this.isLoadingMore = false;
    }
  }

  loadProductDetails(productId: string) {
    this.loading = true;
    this.errorMessage = '';
    
    this.apiService.getProductDetails(productId).subscribe({
      next: (data) => {
        this.product = data;
        // Apply filters and initialize pagination
        this.applyShareFilters();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error loading product details:', err);
        this.errorMessage = err.error?.message || 'Error loading product details';
        this.loading = false;
      }
    });
  }

  switchTab(tab: 'details' | 'requirements' | 'share' | 'quote') {
    // Prevent suppliers from accessing 'share' or 'quote' tabs
    if (this.authService.isSupplier() && (tab === 'share' || tab === 'quote')) {
      return;
    }
    this.activeTab = tab;
    if (tab === 'share') {
      this.applyShareFilters();
      // Re-attach scroll listener when switching to share tab
      setTimeout(() => {
        this.attachScrollListener();
      }, 100);
    } else if (tab === 'quote') {
      // Load quotes when quote tab is clicked
      if (this.product?._id && this.quotes.length === 0) {
        this.loadQuotes();
      } else {
        this.applyQuoteFilters();
      }
    }
  }

  formatDate(date: any): string {
    if (!date) return '-';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  formatDateForInput(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getCategoryDisplay(category: any): string {
    if (!category) return 'N/A';
    const categoryId = category.categoryId || 'N/A';
    return `${category.name} (${categoryId})`;
  }

  getProductImageUrl(product: any): string {
    if (!product) return '';
    
    // Use image field if available (new field - priority)
    if (product.image) {
      // If already a full URL, return as is
      if (product.image.startsWith('http://') || product.image.startsWith('https://')) {
        return product.image;
      }
      // Otherwise construct S3 URL (if needed)
      return product.image;
    }
    
    // Fallback: Check if product has image in specifications (legacy)
    if (product.specifications && Array.isArray(product.specifications)) {
      const imageSpec = product.specifications.find((spec: any) => 
        spec.keyType === 'Upload' && spec.value
      );
      if (imageSpec && imageSpec.value) {
        return imageSpec.value;
      }
    }
    
    // Fallback: Check if category has image
    if (product.category?.image) {
      return product.category.image;
    }
    
    return '';
  }

  onProductImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
      const placeholder = img.nextElementSibling as HTMLElement;
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    }
  }

  viewProductImage() {
    const imageUrl = this.getProductImageUrl(this.product);
    if (imageUrl) {
      this.modalImageUrl = imageUrl;
      // Extract filename from URL or use default
      this.modalImageName = this.extractFileNameFromUrl(imageUrl) || (this.product?.name ? `${this.product.name}.jpg` : 'Product Image');
      this.showImageModal = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
  }

  closeImageModal() {
    this.showImageModal = false;
    this.modalImageUrl = '';
    this.modalImageName = '';
    // Restore body scroll
    document.body.style.overflow = '';
  }

  downloadImage() {
    if (this.modalImageUrl) {
      const link = document.createElement('a');
      link.href = this.modalImageUrl;
      link.download = this.modalImageName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  extractFileNameFromUrl(url: string): string {
    try {
      // Extract filename from URL
      const urlParts = url.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
      return fileName || 'image.jpg';
    } catch (error) {
      return 'image.jpg';
    }
  }

  getQuoteStatusClass(status: string): string {
    switch(status) {
      case 'Active':
      case 'Submitted':
      case 'Under Review':
      case 'Approved':
        return 'status-active-quote';
      case 'Rejected':
      case 'Expired':
        return 'status-expired-quote';
      case 'Unshare':
        return 'status-unshared';
      case 'Yet to Quote':
      case 'Draft':
      default:
        return 'status-pending';
    }
  }

  getQuoteStatusText(quote: any): string {
    if (!quote) return 'Yet to Quote';
    const status = quote.status || 'Draft';
    // Capitalize first letter and keep rest as is
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  // Share tab filters
  onShareSearch() {
    this.applyShareFilters();
  }

  applyShareFilters() {
    if (!this.product || !this.product.suppliersWithDetails) {
      this.allFilteredSuppliers = [];
      this.displayedSuppliers = [];
      this.resetPagination();
      return;
    }

    // Trim search term
    const searchTerm = (this.searchTerm || '').trim().toLowerCase();

    // Apply filters to get all matching suppliers
    // Share tab: Only show quotes where validityDate is empty AND status is "Yet to Quote" or "Expired"
    // Also show suppliers without quotes (yet to quote)
    this.allFilteredSuppliers = this.product.suppliersWithDetails.filter((item: any) => {
      const supplier = item.supplier;
      
      // Share tab requirement: Only show quotes where validityDate is empty AND status is "Yet to Quote" or "Expired"
      // Also show suppliers without quotes (they are yet to quote)
      if (item.quote) {
        const validityDate = item.quote.validityDate;
        const status = item.quote.status || '';
        // Check if validityDate is empty (null, undefined, or empty string)
        const isValidityDateEmpty = !validityDate || validityDate === '' || validityDate === null || validityDate === undefined;
        // Check if status is "Yet to Quote" or "Expired"
        const isValidStatus = status === 'Yet to Quote' || status === 'Expired';
        
        // If quote exists, it must have empty validityDate AND status must be "Yet to Quote" or "Expired"
        if (!isValidityDateEmpty || !isValidStatus) {
          return false;
        }
      }
      // If no quote exists, show the supplier (they are yet to quote)
      
      // Search by Supplier ID and Company Name
      let matchesSearch = true;
      if (searchTerm) {
        const supplierId = (supplier?.supplierId || '').toString().toLowerCase();
        const companyName = (supplier?.companyName || '').toString().toLowerCase();
        
        matchesSearch = supplierId.includes(searchTerm) || companyName.includes(searchTerm);
      }
      
      const matchesSupplierStatus = !this.selectedSupplierStatus || 
        (this.selectedSupplierStatus === 'Active' && supplier.isActive) ||
        (this.selectedSupplierStatus === 'Inactive' && !supplier.isActive);
      
      // Quote status filter
      let matchesQuoteStatus = true;
      if (this.selectedQuoteStatus === 'Active') {
        // Active quotes: status is Active, Submitted, Under Review, or Approved, and not unshared
        matchesQuoteStatus = item.quote && 
                            !item.quote.isUnshared && 
                            item.quote.status && 
                            (item.quote.status === 'Active' || 
                             item.quote.status === 'Submitted' || 
                             item.quote.status === 'Under Review' || 
                             item.quote.status === 'Approved');
      } else if (this.selectedQuoteStatus === 'Yet to Quote') {
        // Yet to Quote: no quote, or quote with status "Yet to Quote" or "Draft", and not unshared
        matchesQuoteStatus = !item.quote || 
                            (item.quote && 
                             !item.quote.isUnshared && 
                             (item.quote.status === 'Yet to Quote' || 
                              item.quote.status === 'Draft' || 
                              !item.quote.status));
      } else if (this.selectedQuoteStatus === 'Expired') {
        // Expired quotes: status is Expired, or supplierValidDateStatus is Expired, and not unshared
        matchesQuoteStatus = item.quote && 
                            !item.quote.isUnshared && 
                            (item.quote.status === 'Expired' || 
                             item.quote.supplierValidDateStatus === 'Expired');
      }

      return matchesSearch && matchesSupplierStatus && matchesQuoteStatus;
    });

    // Reset pagination and load first page
    this.resetPagination();
    this.loadFirstPage();
  }

  resetPagination() {
    this.currentPage = 1;
    this.totalPages = Math.ceil(this.allFilteredSuppliers.length / this.itemsPerPage);
    this.hasMoreData = this.allFilteredSuppliers.length > this.itemsPerPage;
    this.displayedSuppliers = [];
  }

  loadFirstPage() {
    const endIndex = Math.min(this.itemsPerPage, this.allFilteredSuppliers.length);
    this.displayedSuppliers = this.allFilteredSuppliers.slice(0, endIndex);
    this.currentPage = 1;
    this.hasMoreData = this.allFilteredSuppliers.length > this.itemsPerPage;
    
    // Re-attach scroll listener after data loads
    setTimeout(() => {
      this.attachScrollListener();
    }, 50);
  }

  resetShareFilters() {
    this.searchTerm = '';
    this.selectedSupplierStatus = '';
    this.selectedQuoteStatus = '';
    this.applyShareFilters();
  }

  getSharedCount(): number {
    return this.product?.suppliersWithDetails?.length || 0;
  }

  getQuotedCount(): number {
    return this.product?.suppliersWithDetails?.filter((item: any) => item.quote)?.length || 0;
  }

  getYetToQuoteCount(): number {
    return this.getSharedCount() - this.getQuotedCount();
  }

  getFilteredSharedCount(): number {
    return this.allFilteredSuppliers.length;
  }

  getFilteredQuotedCount(): number {
    // Count suppliers that have an active quote (not unshared, and status is not "Yet to Quote")
    return this.allFilteredSuppliers.filter((item: any) => {
      return item.quote && 
             !item.quote.isUnshared && 
             item.quote.status && 
             item.quote.status !== 'Yet to Quote';
    }).length;
  }

  getFilteredYetToQuoteCount(): number {
    // Count suppliers that have a quote with status "Yet to Quote" and it's not unshared
    return this.allFilteredSuppliers.filter((item: any) => {
      return item.quote && 
             !item.quote.isUnshared && 
             (item.quote.status === 'Yet to Quote' || item.quote.status === 'Draft');
    }).length;
  }

  getFilteredUnsharedCount(): number {
    // Count suppliers whose quotes are unshared
    return this.allFilteredSuppliers.filter((item: any) => {
      return item.quote && item.quote.isUnshared === true;
    }).length;
  }

  // Action menu methods
  toggleActionMenu(supplierId: string) {
    // Close all other menus
    Object.keys(this.showActionMenu).forEach(key => {
      if (key !== supplierId) {
        this.showActionMenu[key] = false;
      }
    });
    // Toggle current menu
    this.showActionMenu[supplierId] = !this.showActionMenu[supplierId];
  }

  // Close action menu when clicking outside
  closeActionMenus() {
    Object.keys(this.showActionMenu).forEach(key => {
      this.showActionMenu[key] = false;
    });
  }

  // Unshare quote
  unshareQuote(quoteId: string, supplierId: string) {
    // Find the supplier and quote details
    const supplierItem = this.displayedSuppliers.find(item => item.supplier._id === supplierId);
    if (!supplierItem || !supplierItem.quote) {
      return;
    }

    this.quoteToUnshare = supplierItem.quote;
    this.supplierToUnshare = supplierItem.supplier;
    
    // Create message: "Do you want to unshare the [Product Name] for [Company Name] ([Supplier ID])?"
    const productName = this.product?.name || 'this product';
    const companyName = this.supplierToUnshare.companyName || this.supplierToUnshare.name || 'this supplier';
    const supplierIdDisplay = this.supplierToUnshare.supplierId || supplierId;
    
    this.unshareModalMessage = `Do you want to unshare the ${productName} for ${companyName} (${supplierIdDisplay})?`;
    this.showUnshareModal = true;
    this.closeActionMenus();
  }

  // Confirm unshare action
  onConfirmUnshare() {
    if (!this.quoteToUnshare || !this.quoteToUnshare._id) {
      this.closeUnshareModal();
      return;
    }

    this.apiService.unshareQuote(this.quoteToUnshare._id).subscribe({
      next: (response) => {
        // Reload product details to get updated quote status
        const productId = this.route.snapshot.paramMap.get('id');
        if (productId) {
          this.loadProductDetails(productId);
        }
        this.closeUnshareModal();
        this.errorMessage = '';
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Error unsharing quote';
        this.closeUnshareModal();
        setTimeout(() => {
          this.errorMessage = '';
        }, 5000);
      }
    });
  }

  // Close unshare modal
  closeUnshareModal() {
    this.showUnshareModal = false;
    this.quoteToUnshare = null;
    this.supplierToUnshare = null;
    this.unshareModalMessage = '';
  }

  // Reshare quote
  reshareQuote(quoteId: string, supplierId: string) {
    if (!confirm('Are you sure you want to reshare this quote? The supplier will have access to this quote again.')) {
      return;
    }

    this.apiService.reshareQuote(quoteId).subscribe({
      next: (response) => {
        // Reload product details to get updated quote status
        const productId = this.route.snapshot.paramMap.get('id');
        if (productId) {
          this.loadProductDetails(productId);
        }
        this.closeActionMenus();
        this.errorMessage = '';
      },
      error: (err) => {
        this.errorMessage = err.error?.message || 'Error resharing quote';
        this.closeActionMenus();
        setTimeout(() => {
          this.errorMessage = '';
        }, 5000);
      }
    });
  }

  goBack() {
    this.router.navigate(['/product']);
  }

  printActiveTab() {
    // Add print class to body to trigger print styles
    document.body.classList.add('printing');
    
    // Wait a moment for styles to apply, then print
    setTimeout(() => {
      window.print();
      // Remove print class after printing
      setTimeout(() => {
        document.body.classList.remove('printing');
      }, 500);
    }, 100);
  }

  getUserRole(user: any): string {
    if (!user) return '';
    if (user.userType === 'Admin') return 'Admin';
    if (user.userType === 'ProcurementManager') return 'Procurement Manager';
    if (user.userType === 'Supplier') return 'Supplier';
    return user.userType || '';
  }

  formatSpecValue(value: string): string {
    if (!value) return '';
    // If it's a bullet point format (newlines), convert to HTML list
    if (value.includes('\n')) {
      const lines = value.split('\n').filter(line => line.trim());
      return '<ul>' + lines.map(line => `<li>${line.trim()}</li>`).join('') + '</ul>';
    }
    return value;
  }

  getFileName(url: string): string {
    if (!url) return 'Document.pdf';
    const parts = url.split('/');
    return parts[parts.length - 1] || 'Document.pdf';
  }

  getShippingAddress(): string {
    if (!this.product?.shippingDetails) return 'N/A';
    const addr1 = this.product.shippingDetails.addressLine1 || '';
    const addr2 = this.product.shippingDetails.addressLine2 || '';
    if (addr1 && addr2) {
      return `${addr1}, ${addr2}`;
    }
    return addr1 || addr2 || 'N/A';
  }

  formatNumber(num: number): string {
    if (!num) return '0';
    return num.toLocaleString('en-IN');
  }

  // Quote tab methods
  loadQuotes() {
    if (!this.product?._id) return;
    
    this.quotesLoading = true;
    this.apiService.getQuotesByProduct(this.product._id).subscribe({
      next: (response: ApiResponse<any> | any) => {
        const quotesData = extractData(response);
        this.quotes = Array.isArray(quotesData) ? quotesData : [];
        this.applyQuoteFilters();
        this.quotesLoading = false;
      },
      error: (err) => {
        console.error('Error loading quotes:', err);
        this.errorMessage = extractErrorMessage(err);
        this.quotesLoading = false;
      }
    });
  }

  applyQuoteFilters() {
    let filtered = [...this.quotes];

    // Quote tab requirement: Only show quotes where supplierValidDateStatus is not empty AND validityDate is given
    filtered = filtered.filter(quote => {
      const hasValidDateStatus = quote.supplierValidDateStatus && 
                                 quote.supplierValidDateStatus.trim() !== '';
      const hasValidityDate = quote.validityDate && quote.validityDate.trim() !== '';
      return hasValidDateStatus && hasValidityDate;
    });

    // Search filter
    if (this.quoteSearchTerm) {
      const searchLower = this.quoteSearchTerm.toLowerCase();
      filtered = filtered.filter(quote => {
        const quoteId = (quote.quoteId || '').toLowerCase();
        const supplierName = (quote.supplier?.name || quote.supplier?.companyName || '').toLowerCase();
        const supplierId = (quote.supplier?.supplierId || '').toLowerCase();
        return quoteId.includes(searchLower) || supplierName.includes(searchLower) || supplierId.includes(searchLower);
      });
    }

    // Status filter
    if (this.quoteStatusFilter) {
      filtered = filtered.filter(quote => {
        if (this.quoteStatusFilter === 'Active') {
          return quote.supplierValidDateStatus === 'Active';
        } else if (this.quoteStatusFilter === 'Expired') {
          return quote.supplierValidDateStatus === 'Expired';
        }
        return true;
      });
    }

    this.filteredQuotes = filtered;
    // Select all quotes by default
    this.selectAllQuotesByDefault();
  }

  selectAllQuotesByDefault() {
    // Clear existing selections and select all filtered quotes
    this.selectedQuotes.clear();
    this.filteredQuotes.forEach(quote => {
      if (quote._id) {
        this.selectedQuotes.add(quote._id);
      }
    });
  }

  onQuoteSearchChange() {
    this.applyQuoteFilters();
  }

  onQuoteStatusFilterChange() {
    this.applyQuoteFilters();
  }

  resetQuoteFilters() {
    this.quoteSearchTerm = '';
    this.quoteStatusFilter = '';
    this.applyQuoteFilters();
  }

  toggleQuoteSelection(quoteId: string) {
    if (this.selectedQuotes.has(quoteId)) {
      this.selectedQuotes.delete(quoteId);
    } else {
      this.selectedQuotes.add(quoteId);
    }
  }

  toggleAllQuotes() {
    if (this.selectedQuotes.size === this.filteredQuotes.length) {
      this.selectedQuotes.clear();
    } else {
      this.filteredQuotes.forEach(quote => {
        this.selectedQuotes.add(quote._id);
      });
    }
  }

  getSupplierValidDateStatusClass(status: string): string {
    if (status === 'Active') return 'status-active';
    if (status === 'Expired') return 'status-expired';
    return '';
  }

  getSupplierValidDateStatusText(status: string): string {
    if (status === 'Active') return 'Quoted';
    if (status === 'Expired') return 'Expired';
    return '';
  }

  getQuotePriceRange(quote: any): { min: number; max: number } {
    if (!quote.quantityPrices || quote.quantityPrices.length === 0) {
      return { min: 0, max: 0 };
    }

    // Filter out null/undefined/zero prices and sort by quantity
    const validPrices = quote.quantityPrices
      .filter((qp: any) => qp.unitPrice && qp.unitPrice > 0 && qp.quantity && qp.quantity > 0)
      .map((qp: any) => ({
        quantity: qp.quantity,
        unitPrice: qp.unitPrice,
        totalPrice: qp.totalPrice || (qp.quantity * qp.unitPrice)
      }))
      .sort((a: any, b: any) => a.quantity - b.quantity); // Sort by quantity ascending

    if (validPrices.length === 0) {
      return { min: 0, max: 0 };
    }

    const minPrice = Math.min(...validPrices.map((p: any) => p.totalPrice));
    const maxPrice = Math.max(...validPrices.map((p: any) => p.totalPrice));

    return { min: minPrice, max: maxPrice };
  }

  getShippingTermDisplay(quote: any): string {
    // Check both quote.shippingDetails and quote.productDetails.shippingDetails
    const shippingDetails = quote.shippingDetails || quote.productDetails?.shippingDetails;
    if (!shippingDetails) return 'N/A';
    
    const term = shippingDetails.shippingTerm || '';
    if (term === 'DELIVERED') {
      const city = shippingDetails.city || '';
      const state = shippingDetails.state || '';
      const country = shippingDetails.country || '';
      const pinCode = shippingDetails.pinCode || shippingDetails.pincode || '';
      return `DELIVERED ${city}, ${state}, ${country}, PIN: ${pinCode}`;
    } else if (term === 'CIF') {
      const country = shippingDetails.country || '';
      const mappedPort = shippingDetails.mappedPort || '';
      return `CIF ${country}, Port: ${mappedPort}`;
    }
    return term;
  }

  compareQuotes() {
    if (this.selectedQuotes.size < 2) {
      alert('Please select at least 2 quotes to compare');
      return;
    }
    
    // Get selected quotes
    const selectedQuoteIds = Array.from(this.selectedQuotes);
    const selectedQuotesData = this.filteredQuotes.filter(quote => 
      selectedQuoteIds.includes(quote._id)
    );
    
    if (selectedQuotesData.length < 2) {
      alert('Please select at least 2 quotes to compare');
      return;
    }
    
    // Store data in sessionStorage as backup
    sessionStorage.setItem('quoteCompareData', JSON.stringify({
      product: this.product,
      quotes: selectedQuotesData
    }));
    
    // Navigate to quote compare component with data
    this.router.navigate(['/products/compare-quotes'], {
      state: {
        product: this.product,
        quotes: selectedQuotesData
      }
    });
  }
  

  viewQuoteDetails(quote: any) {
    // Navigate to quote details page
    this.router.navigate(['/quote/view', quote._id]);
  }

  getFileSize(url: string): string {
    // File size is not stored in the database currently
    // In a real implementation, you might want to:
    // 1. Store file size in the database when uploading
    // 2. Make a HEAD request to S3 to get Content-Length header
    // For now, return a placeholder that matches the image format
    if (!url) return 'N/A';
    return 'N/A'; // Placeholder - can be enhanced to fetch from S3 or database
  }
}

